import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { WorkflowDTO, CaptureMode, DocumentType } from '@facephi/sdk-web-react';

import { useRecoilValue } from 'recoil';
import { useVariables, useWorkflow } from '../hooks';
import {
  documentTypeState,
  logImagesState,
  previewCaptureState,
  countryState,
  recordOnboardingState,
} from '../states/atoms';
import { DEFAULT_COUNTRY, TypeDocument } from '../states/constants';

type IProps = {
  children: React.ReactNode;
};

type ContextProps = {
  getWorkflow(): WorkflowDTO;
};

const Ctx = createContext<ContextProps>({
  getWorkflow: () => [] as WorkflowDTO,
});

export const ConfigProvider = ({ children }: IProps) => {
  const { loading, getWorkflow: getConfigWorkflow } = useWorkflow();
  const { licenseKeyThirdParty } = useVariables();
  const documentType = useRecoilValue(documentTypeState);
  const previewCapture = useRecoilValue(previewCaptureState);
  const logImages = useRecoilValue(logImagesState);
  const country = useRecoilValue(countryState);
  const recordOnboarding = useRecoilValue(recordOnboardingState);
  const [captureMode, setCaptureMode] = useState<CaptureMode>(CaptureMode.Full);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [licenseKey, setLicenseKey] = useState<string | undefined>(
    licenseKeyThirdParty
  );

  const countryConfig = useMemo(
    () => (country === DEFAULT_COUNTRY ? '' : country),
    [country]
  );

  const normalizeDocumentType = (documentType: TypeDocument) => {
    switch (documentType) {
      case TypeDocument.custom:
        return DocumentType.Autodetect;
      case TypeDocument.driverLicense:
        return DocumentType.DriverLicense;
      case TypeDocument.id:
        return DocumentType.IDCard;
      case TypeDocument.passport:
        return DocumentType.Passport;
      case TypeDocument.residentID:
        return DocumentType.ResidentID;
      default:
        return DocumentType.Autodetect;
    }
  };

  useEffect(() => {
    if (documentType === TypeDocument.passport) {
      setCaptureMode(CaptureMode.Front);
    } else {
      setCaptureMode(CaptureMode.Full);
    }
  }, [documentType]);

  const getWorkflow = () => {
    const workflow = getConfigWorkflow({
      previewCapture,
      logImages,
      country: countryConfig,
      captureMode: captureMode,
      licenseKey: licenseKey,
      retryOnlyCurrentSide: true,
      documentType: normalizeDocumentType(documentType),
    });

    return !recordOnboarding
      ? workflow
      : [
          {
            id: 'd9c69588-0176-472f-9b79-516ad379a07b',
            data: {
              widgetId: '6152fab3c01f0c1eb6deedd0',
              widgetName: 'VIDEO_RECORDING',
              configuration: {},
            },
          },
          ...workflow,
        ];
  };

  return (
    <Ctx.Provider
      value={{
        getWorkflow,
      }}
    >
      {!loading && children}
    </Ctx.Provider>
  );
};

export const useConfig = () => useContext(Ctx);
